/**
 * useToolboxDragDrop - Hook for drag-and-drop from toolbox to canvas
 *
 * This hook manages the drag-and-drop interaction between the toolbox panel
 * and the workflow canvas. It handles:
 * - Setting drag data on drag start
 * - Processing drop events on the canvas
 * - Converting screen coordinates to flow coordinates
 * - Creating nodes at the drop location
 * - Smart validation based on operation type and drop zone
 */

import { useCallback, useState } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { OperationDefinition } from '@/types/operations';
import type { Action } from '@/types/workflowDefinition';
import { findScopeContext } from '@/stores/workflowStore';
import { isForEachFull } from '@/hooks/useForEachConstraint';
import {
  FOREACH_SINGLE_ACTION_MESSAGE,
  FOREACH_NESTED_MESSAGE,
} from '@/utils/validation';

/**
 * MIME type for operation data in drag transfer
 * Using a custom MIME type allows us to:
 * 1. Detect valid drops (only accept our operation data)
 * 2. Avoid conflicts with other drag sources
 */
export const OPERATION_DRAG_TYPE = 'application/x-otto-operation';

/**
 * Extended drag category for smart drag-drop validation
 * Maps operation types to drag behavior categories
 */
export type DragCategory = 'trigger' | 'action' | 'agent' | 'control' | 'mcp' | 'subworkflow';

/**
 * Drop zone types for validation
 */
export type DropZone = 'canvas' | 'agent-tools' | 'invalid';

/**
 * Validation result for drop operations
 */
export interface DropValidationResult {
  allowed: boolean;
  reason?: string;
}

/**
 * Get the drag category for an operation
 */
export function getDragCategory(operation: OperationDefinition): DragCategory {
  // Check for special operation types first
  if (operation.type === 'Agent' || operation.id === 'agent') {
    return 'agent';
  }
  if (operation.type === 'MCP' || operation.id?.startsWith('mcp_')) {
    return 'mcp';
  }
  if (operation.type === 'Workflow' || operation.id === 'invoke_workflow' || operation.id === 'subworkflow') {
    return 'subworkflow';
  }

  // Map standard categories
  switch (operation.category) {
    case 'trigger':
      return 'trigger';
    case 'control':
      return 'control';
    case 'action':
    default:
      return 'action';
  }
}

/**
 * Scope info for the drop target, used when dropping inside a control flow scope
 */
export interface DropTargetScope {
  /** Name of the parent control flow action (e.g., ForEach action name) */
  parentActionName: string;
  /** Type of branch within the scope (e.g., 'loop' for ForEach body) */
  branchType: string;
}

/**
 * Workflow state for validation context
 */
export interface WorkflowValidationState {
  /** Whether the workflow already has a trigger */
  hasTrigger: boolean;
  /** The current drop zone being hovered */
  dropZone: DropZone;
  /** If dropping inside a control flow scope, the scope info */
  dropTargetScope?: DropTargetScope;
  /** Top-level workflow actions, needed for scope constraint checks */
  workflowActions?: Record<string, Action>;
}

/**
 * Validate if an operation can be dropped in a given context
 */
export function validateDrop(
  operation: OperationDefinition,
  state: WorkflowValidationState
): DropValidationResult {
  const category = getDragCategory(operation);
  const { hasTrigger, dropZone } = state;

  // Invalid drop zone - always reject
  if (dropZone === 'invalid') {
    return { allowed: false, reason: 'Cannot drop here' };
  }

  // Block nested ForEach: prevent dropping ForEach inside any ForEach scope
  if (state.dropTargetScope && operation.type === 'Foreach') {
    const isDirectLoop = state.dropTargetScope.branchType === 'loop';
    const isNestedInForEach = !isDirectLoop && state.workflowActions
      ? findScopeContext(state.dropTargetScope.parentActionName, state.workflowActions) !== undefined
      : false;

    if (isDirectLoop || isNestedInForEach) {
      return { allowed: false, reason: FOREACH_NESTED_MESSAGE };
    }
  }

  // ForEach single-action constraint: block drops into a ForEach that already has an action
  if (state.dropTargetScope?.branchType === 'loop' && state.workflowActions) {
    if (isForEachFull(state.workflowActions, state.dropTargetScope.parentActionName)) {
      return { allowed: false, reason: FOREACH_SINGLE_ACTION_MESSAGE };
    }
  }

  // Trigger rules
  if (category === 'trigger') {
    if (hasTrigger) {
      return { allowed: false, reason: 'Workflow already has a trigger' };
    }
    if (dropZone === 'agent-tools') {
      return { allowed: false, reason: 'Triggers cannot be agent tools' };
    }
    return { allowed: true };
  }

  // MCP rules - only agent tools zone
  if (category === 'mcp') {
    if (dropZone !== 'agent-tools') {
      return { allowed: false, reason: 'MCP tools can only be added to agents' };
    }
    return { allowed: true };
  }

  // Agent rules - cannot be added as tool to another agent
  if (category === 'agent') {
    if (dropZone === 'agent-tools') {
      return { allowed: false, reason: 'Cannot nest agents as tools' };
    }
    return { allowed: true };
  }

  // Sub-workflow - can be added to canvas or agent tools
  if (category === 'subworkflow') {
    return { allowed: true };
  }

  // Control flow - canvas only
  if (category === 'control') {
    if (dropZone === 'agent-tools') {
      return { allowed: false, reason: 'Control flow cannot be agent tools' };
    }
    return { allowed: true };
  }

  // Actions - canvas only (use Actions panel for agent tools)
  if (category === 'action') {
    if (dropZone === 'agent-tools') {
      return { allowed: false, reason: 'Use Actions menu to add tools' };
    }
    return { allowed: true };
  }

  return { allowed: true };
}

/**
 * Check if an operation is draggable from the toolbox
 */
export function isOperationDraggable(operation: OperationDefinition): boolean {
  const category = getDragCategory(operation);
  // MCP is not draggable yet (coming soon)
  if (category === 'mcp') {
    return false;
  }
  return true;
}

/**
 * Set up drag data on a toolbox item
 */
export function setOperationDragData(
  event: React.DragEvent,
  operation: OperationDefinition
): void {
  // Set the custom MIME type with serialized operation
  event.dataTransfer.setData(OPERATION_DRAG_TYPE, JSON.stringify(operation));

  // Also set plain text as fallback (operation name)
  event.dataTransfer.setData('text/plain', operation.name);

  // Set the drag effect
  event.dataTransfer.effectAllowed = 'copy';
}

/**
 * Extract operation data from a drop event
 * Returns null if the drop doesn't contain valid operation data
 */
export function getOperationFromDrop(
  event: React.DragEvent | DragEvent
): OperationDefinition | null {
  try {
    const data = event.dataTransfer?.getData(OPERATION_DRAG_TYPE);
    if (!data) return null;
    return JSON.parse(data) as OperationDefinition;
  } catch {
    return null;
  }
}

/**
 * Check if a drag event contains valid operation data
 */
export function isOperationDrag(event: React.DragEvent | DragEvent): boolean {
  return event.dataTransfer?.types.includes(OPERATION_DRAG_TYPE) ?? false;
}

/**
 * Hook return type
 */
export interface UseToolboxDragDropReturn {
  /** Whether an operation is currently being dragged over the canvas */
  isDraggingOver: boolean;
  /** Whether the current drag is valid for dropping */
  isValidDrop: boolean;
  /** Reason why the drop is invalid (if applicable) */
  invalidDropReason: string | undefined;
  /** Handle drag over event on the canvas */
  handleDragOver: (event: React.DragEvent | DragEvent) => void;
  /** Handle drag leave event on the canvas */
  handleDragLeave: () => void;
  /** Handle drop event on the canvas */
  handleDrop: (event: React.DragEvent | DragEvent) => void;
  /** Create drag start handler for a toolbox item */
  createDragStartHandler: (
    operation: OperationDefinition
  ) => (event: React.DragEvent) => void;
}

/**
 * Hook options
 */
export interface UseToolboxDragDropOptions {
  /** Called when an operation is dropped on the canvas */
  onOperationDrop: (
    operation: OperationDefinition,
    position: { x: number; y: number }
  ) => void;
  /** Whether drag-drop is enabled (e.g., disabled in monitoring mode) */
  enabled?: boolean;
  /** Whether the workflow already has a trigger */
  hasTrigger?: boolean;
  /** Current drop zone type (default: 'canvas') */
  dropZone?: DropZone;
  /** Called when drop is rejected with reason */
  onDropRejected?: (operation: OperationDefinition, reason: string) => void;
}

/**
 * Hook for managing drag-and-drop from toolbox to canvas
 *
 * @example
 * ```tsx
 * const { isDraggingOver, handleDragOver, handleDragLeave, handleDrop } =
 *   useToolboxDragDrop({
 *     onOperationDrop: (operation, position) => {
 *       addAction(operation, position);
 *     },
 *   });
 *
 * return (
 *   <ReactFlow
 *     onDragOver={handleDragOver}
 *     onDragLeave={handleDragLeave}
 *     onDrop={handleDrop}
 *   >
 *     ...
 *   </ReactFlow>
 * );
 * ```
 */
export function useToolboxDragDrop({
  onOperationDrop,
  enabled = true,
  hasTrigger = false,
  dropZone = 'canvas',
  onDropRejected,
}: UseToolboxDragDropOptions): UseToolboxDragDropReturn {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isValidDrop, setIsValidDrop] = useState(true);
  const [invalidDropReason, setInvalidDropReason] = useState<string | undefined>();
  const { screenToFlowPosition } = useReactFlow();

  /**
   * Handle drag over - allow drop if it's a valid operation drag
   */
  const handleDragOver = useCallback(
    (event: React.DragEvent | DragEvent) => {
      if (!enabled) return;

      // Check if this is a valid operation drag
      if (!isOperationDrag(event)) return;

      // Prevent default to allow drop
      event.preventDefault();
      event.stopPropagation();

      // Try to get operation for validation (may not be available during dragover)
      // Note: getData() only works in drop event, not dragover due to browser security
      // So we validate on drop, but set visual feedback based on drag type
      setIsDraggingOver(true);

      // Set visual feedback - we'll show valid by default during drag
      // and validate fully on drop
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [enabled]
  );

  /**
   * Handle drag leave - reset visual state
   */
  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(false);
    setIsValidDrop(true);
    setInvalidDropReason(undefined);
  }, []);

  /**
   * Handle drop - validate and create node at drop location
   */
  const handleDrop = useCallback(
    (event: React.DragEvent | DragEvent) => {
      if (!enabled) return;

      event.preventDefault();
      event.stopPropagation();
      setIsDraggingOver(false);

      // Extract operation from drag data
      const operation = getOperationFromDrop(event);
      if (!operation) return;

      // Validate the drop
      const validationState: WorkflowValidationState = {
        hasTrigger,
        dropZone,
      };
      const validation = validateDrop(operation, validationState);

      if (!validation.allowed) {
        setIsValidDrop(false);
        setInvalidDropReason(validation.reason);
        onDropRejected?.(operation, validation.reason || 'Invalid drop');

        // Reset after a short delay
        setTimeout(() => {
          setIsValidDrop(true);
          setInvalidDropReason(undefined);
        }, 2000);
        return;
      }

      // Reset validation state
      setIsValidDrop(true);
      setInvalidDropReason(undefined);

      // Convert screen position to flow position
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Callback to create the node
      onOperationDrop(operation, position);
    },
    [enabled, screenToFlowPosition, onOperationDrop, hasTrigger, dropZone, onDropRejected]
  );

  /**
   * Create a drag start handler for a specific operation
   */
  const createDragStartHandler = useCallback(
    (operation: OperationDefinition) => (event: React.DragEvent) => {
      if (!enabled) {
        event.preventDefault();
        return;
      }
      setOperationDragData(event, operation);
    },
    [enabled]
  );

  return {
    isDraggingOver,
    isValidDrop,
    invalidDropReason,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    createDragStartHandler,
  };
}

/**
 * Hook return type for agent tool drop zone
 */
export interface UseAgentToolDropZoneReturn {
  /** Whether an operation is being dragged over the tool zone */
  isDraggingOver: boolean;
  /** Whether the current drag is valid for this drop zone */
  isValidDrop: boolean;
  /** Reason why the drop is invalid */
  invalidDropReason: string | undefined;
  /** Props to spread on the drop zone element */
  dropZoneProps: {
    onDragOver: (event: React.DragEvent) => void;
    onDragLeave: (event: React.DragEvent) => void;
    onDrop: (event: React.DragEvent) => void;
  };
}

/**
 * Hook options for agent tool drop zone
 */
export interface UseAgentToolDropZoneOptions {
  /** Agent node ID */
  agentId: string;
  /** Called when a valid tool is dropped */
  onToolDrop: (operation: OperationDefinition, agentId: string) => void;
  /** Whether drops are enabled */
  enabled?: boolean;
  /** Called when drop is rejected */
  onDropRejected?: (operation: OperationDefinition, reason: string) => void;
}

/**
 * Hook for handling drag-drop on agent tool zones
 *
 * Only accepts operations that can be agent tools (subworkflow, mcp).
 * Used by AgentNode to enable dropping tools directly onto the agent.
 */
export function useAgentToolDropZone({
  agentId,
  onToolDrop,
  enabled = true,
  onDropRejected,
}: UseAgentToolDropZoneOptions): UseAgentToolDropZoneReturn {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isValidDrop, setIsValidDrop] = useState(true);
  const [invalidDropReason, setInvalidDropReason] = useState<string | undefined>();

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!enabled) return;
      if (!isOperationDrag(event)) return;

      event.preventDefault();
      event.stopPropagation();
      setIsDraggingOver(true);

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [enabled]
  );

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    // Only reset if we're actually leaving the drop zone (not entering a child)
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement;
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }
    setIsDraggingOver(false);
    setIsValidDrop(true);
    setInvalidDropReason(undefined);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (!enabled) return;

      event.preventDefault();
      event.stopPropagation();
      setIsDraggingOver(false);

      const operation = getOperationFromDrop(event);
      if (!operation) return;

      // Validate for agent-tools zone
      const validation = validateDrop(operation, {
        hasTrigger: false, // Not relevant for agent tools
        dropZone: 'agent-tools',
      });

      if (!validation.allowed) {
        setIsValidDrop(false);
        setInvalidDropReason(validation.reason);
        onDropRejected?.(operation, validation.reason || 'Invalid drop');

        setTimeout(() => {
          setIsValidDrop(true);
          setInvalidDropReason(undefined);
        }, 2000);
        return;
      }

      setIsValidDrop(true);
      setInvalidDropReason(undefined);
      onToolDrop(operation, agentId);
    },
    [enabled, agentId, onToolDrop, onDropRejected]
  );

  return {
    isDraggingOver,
    isValidDrop,
    invalidDropReason,
    dropZoneProps: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
